import React from 'react';
import { motion } from 'framer-motion';
import { Badge } from './ui/badge';
import { personalInfo } from '../data/portfolio';
import { Brain, Zap, Layers, Code2 } from 'lucide-react';
import ProfileImage from './ProfileImage';
import ParticleField from './ParticleField';
const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.6, ease: 'easeOut' }
  })
};

const strengths = [
  {
    icon: Brain,
    title: 'AI/ML Systems',
    desc: 'From voice biometrics to recommendation engines, I build production-grade ML pipelines with PyTorch, FAISS, and classical ML techniques.'
  },
  {
    icon: Zap,
    title: 'Automation at Scale',
    desc: 'Designed enterprise automation ecosystems for major banks and retail organisation — processing millions of fraud events and test flows.'
  },
  {
    icon: Code2,
    title: 'Full-Stack Builder',
    desc: 'From LaTeX resume SaaS (buildresume.cv) to mobile apps with Expo — I bring complete product ideas to life end-to-end.'
  },
  {
    icon: Layers,
    title: 'DevOps & Cloud',
    desc: 'Docker, Kubernetes, Google Cloud Run, Azure DevOps — deploying and scaling intelligent systems in production environments.'
  }
];

const AboutSection = () => {
  return (
    <section id="about" className="relative py-24 px-6 md:px-12 lg:px-24 overflow-hidden">
      
      {/* Subtle background glow */}
      <div
        className="absolute top-0 right-0 w-[500px] h-[500px] pointer-events-none"
        style={{ background: 'radial-gradient(circle, hsl(var(--primary) / 0.06) 0%, transparent 70%)' }}
      />
       {/* Grid dots */}
      <div className="absolute inset-0 bg-grid opacity-40 pointer-events-none" />

      {/* Particles */}
      <div className="absolute inset-0">
        <ParticleField />
      </div>
      <div className="max-w-7xl mx-auto">
        {/* Section header */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mb-16 "
        >
          <Badge variant="outline" className="border-primary/30 text-primary bg-primary/5 mb-4 font-mono text-xs uppercase tracking-wider">
            About Me
          </Badge>
          <h2 className="font-heading font-bold text-4xl md:text-5xl text-foreground leading-tight">
            Building AI-Powered
            <br />
            <span className="gradient-text">Products & Systems</span>
          </h2>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Left: Story */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            custom={1}
            viewport={{ once: true }}
            className="space-y-6"
          >
            {/* Small profile image inside About */}
            <div className="flex items-center gap-4 mb-2">
              <ProfileImage size="sm" />
              <div>
                <p className="font-heading font-semibold text-foreground">{personalInfo.name}</p>
                <p className="text-muted-foreground text-xs">{personalInfo.title}</p>
                <p className="text-muted-foreground text-xs">{personalInfo.location}</p>
              </div>
            </div>
           <p className="text-muted-foreground text-base leading-relaxed">
  I'm a <span className="text-foreground font-medium">Technical Lead at <span className="text-primary font-medium">Wipro Canada</span></span> and an M.Tech candidate in Artificial Intelligence at the{' '}
  <span className="text-primary font-medium">Indian Institute of Science (IISc), Bengaluru</span> — one of Asia’s premier research institutions.
</p>

<p className="text-muted-foreground text-base leading-relaxed">
  Over 7+ years, I’ve architected enterprise automation ecosystems across retail, banking, and insurance — designing 
  <span className="text-foreground font-medium"> validation frameworks, distributed microservice test systems, GenAI-powered platforms, and CI/CD intelligence pipelines </span> 
   that support high-scale transaction environments.
</p>

<p className="text-muted-foreground text-base leading-relaxed">
  I operate at the intersection of enterprise engineering and applied AI — leading large-scale automation architecture initiatives while building intelligent systems that bridge research and production.

</p>

            {/* Education highlights */}
            <div className="space-y-3 pt-4">
              <div className="flex items-start gap-3 p-4 glass rounded-xl">
                <div className="w-2 h-2 rounded-full bg-primary mt-2 flex-shrink-0" />
                <div>
                  <div className="text-foreground font-medium text-sm">M.Tech, Artificial Intelligence</div>
                  <div className="text-muted-foreground text-xs mt-0.5">Indian Institute of Science, Bengaluru · 2024–2026</div>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 glass rounded-xl">
                <div className="w-2 h-2 rounded-full bg-secondary mt-2 flex-shrink-0" />
                <div>
                  <div className="text-foreground font-medium text-sm">B.Tech, Electrical Engineering</div>
                  <div className="text-muted-foreground text-xs mt-0.5">National Institute of Technology, Durgapur · 2014-2018</div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right: Strengths grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {strengths.map((item, i) => (
              <motion.div
                key={i}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                custom={i + 2}
                viewport={{ once: true }}
                className="glass glass-hover rounded-2xl p-6 flex flex-col gap-3"
              >
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{
                    background: i % 2 === 0 ? 'hsl(var(--primary) / 0.1)' : 'hsl(var(--secondary) / 0.1)',
                    border: `1px solid hsl(var(--${i % 2 === 0 ? 'primary' : 'secondary'}) / 0.2)`
                  }}
                >
                  <item.icon
                    size={20}
                    style={{ color: `hsl(var(--${i % 2 === 0 ? 'primary' : 'secondary'}))` }}
                  />
                </div>
                <h3 className="font-heading font-semibold text-foreground text-sm">{item.title}</h3>
                <p className="text-muted-foreground text-xs leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
